import ErrorMessage from "common/ErrorMessage/ErrorMessage";
import InfoPopup from "common/InfoPopup/InfoPopup";
import NavigationButtons from "common/NavigationButtons/NavigationButtons";
import RowHeader from "common/RowHeader/RowHeader";
import SelectDropdown from "common/SelectDropdown/SelectDropdown";
import Spinner from "common/Spinner/Spinner";
import { useEffect, useState, useContext } from "react";
import { useLazyCalculateMonthlyPayoutQuery } from "service/APIService";
import { Constants } from "utils/Constants";
import {
  checkFutureDate,
  checkTwoDaysFromNow,
  checkTwoYearsFromNow,
  getDaysInMonth,
  getRequiredIds,
  getYears,
} from "utils/Utilities";

const SelectElection = ({ context }) => {
  const {
    labels,
    navigate,
    selectedDate,
    setSelectedDate,
    sanitize,
    participantDetails,
    investmentDetails,
    cancel,
  } = useContext(context);

  const [days, setDays] = useState([]);
  const [errors, setErrors] = useState(null);

  const [calculateMonthlyPayoutApi, { isLoading }] =
    useLazyCalculateMonthlyPayoutQuery();

  const next = () => {
    setErrors(null);

    calculateMonthlyPayoutApi({
      ...getRequiredIds(participantDetails),
      payoutStartDate: `${String(selectedDate?.month).padStart(2, "0")}/${
        selectedDate?.day
      }/${selectedDate?.year}`,
    })
      .unwrap()
      .then((res) => {
        navigate("/monthly-payout-election/2", {
          state: res,
        });
      })
      .catch((err) => {
        setErrors(err?.data);
      });
  };

  useEffect(() => {
    const totalDays = getDaysInMonth(selectedDate.year, selectedDate.month);
    const newDays = Array.from({ length: totalDays }, (_, i) => i + 1);
    setDays(newDays);
    if (selectedDate.day > totalDays) {
      setSelectedDate((prev) => ({ ...prev, day: totalDays }));
    }
  }, [selectedDate.year, selectedDate.month]);

  useEffect(() => {
    setErrors(null);

    if (
      !checkFutureDate(selectedDate.day, selectedDate.month, selectedDate.year)
    ) {
      setErrors({
        errorMessage: labels?.monthlyPayoutElection?.pastDateError,
      });
    } else if (
      !checkTwoDaysFromNow(
        selectedDate.day,
        selectedDate.month,
        selectedDate.year
      )
    ) {
      setErrors({
        errorMessage: labels?.monthlyPayoutElection?.twoDaysError,
      });
    } else if (
      !checkTwoYearsFromNow(
        selectedDate.day,
        selectedDate.month,
        selectedDate.year
      )
    ) {
      setErrors({
        errorMessage: labels?.monthlyPayoutElection?.twoYearsError,
      });
    }
  }, [selectedDate]);

  return (
    <section className="section-one monthly-payout">
      {isLoading && <Spinner />}
      {errors && <ErrorMessage error={errors} wrapperClassName="mb-3" />}

      <RowHeader
        header={labels?.monthlyPayoutElection?.monthlyPayoutElection}
        wrapperClassName="mb-2"
      />
      <p
        dangerouslySetInnerHTML={sanitize(
          labels?.monthlyPayoutElection?.eligibleToStart
        )}
      />
      <ul
        dangerouslySetInnerHTML={sanitize(
          labels?.monthlyPayoutElection?.listItems
        )}
      ></ul>

      <div className="my-4 fw-bold">
        {labels?.monthlyPayoutElection?.scheduleMonthly}
      </div>

      <div className="">
        <div className="d-flex flex-wrap-mobile mb-3">
          <div className="col-12 col-md-5 col-lg-4 border-bottom-1">
            {labels?.monthlyPayoutElection?.incomeBenefitElection}
            <InfoPopup
              position={"top"}
              content={
                labels?.monthlyPayoutElection?.incomeBenefitElectionPopup
              }
            />
          </div>
          <div className="col-12 py-3 mobile">
            {investmentDetails?.electionInfo?.electionType +
              labels?.monthlyPayoutElection?.option}
          </div>
          <div className="col-1"></div>
          <div className="col-12 col-md-5 col-lg-4 border-bottom-1">
            {labels?.monthlyPayoutElection?.startDate}
            <InfoPopup
              position={"top"}
              content={labels?.monthlyPayoutElection?.startDatePopup}
            />
          </div>
        </div>

        <div className="d-flex align-items-center mb-3">
          <div className="col-4 desktop">
            {investmentDetails?.electionInfo?.electionType +
              labels?.monthlyPayoutElection?.option}
          </div>
          <div className="col-1 desktop"></div>
          <div className="col-12 col-md-5 d-flex flex-wrap-mobile">
            <SelectDropdown
              wrapperClassName="col-12 col-xl-4 me-2 mb-3"
              selectClassName="w-100"
              dropdownData={Constants.MONTHS}
              value={selectedDate.month}
              select={(e) =>
                setSelectedDate({
                  ...selectedDate,
                  month: e,
                })
              }
              optionId="value"
              optionLabel="label"
              ariaLabel="Months Dropdown"
            />
            <SelectDropdown
              wrapperClassName="col-12 col-xl-3 me-2 mb-3"
              selectClassName="w-100"
              dropdownData={days}
              value={selectedDate.day}
              select={(e) => setSelectedDate({ ...selectedDate, day: e })}
              ariaLabel="Days Dropdown"
            />
            <SelectDropdown
              wrapperClassName="col-12 col-xl-3 me-2"
              selectClassName="w-100"
              dropdownData={getYears()}
              value={selectedDate.year}
              select={(e) => setSelectedDate({ ...selectedDate, year: e })}
              ariaLabel="Years Dropdown"
            />
          </div>
        </div>
        <div className="col-10 border-bottom-1 d-flex desktop"></div>
      </div>

      <NavigationButtons
        wrapperClassName="py-5"
        leftLabel={labels?.monthlyPayoutElection?.cancel}
        leftClick={cancel}
        rightLabel={labels?.monthlyPayoutElection?.next}
        rightClick={next}
        rightDisabled={errors}
      />
    </section>
  );
};

export default SelectElection;
